/** Small formatting helpers shared by many pages. */

export function initials(name: string | null | undefined): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/** "Nina Ross" -> "Nina R" (how the legacy portal abbreviates team members). */
export function shortName(fullName: string | null | undefined): string {
  if (!fullName) return ''
  const [first, ...rest] = fullName.trim().split(/\s+/)
  const last = rest.length ? rest[rest.length - 1][0].toUpperCase() : ''
  return last ? `${first} ${last}` : first
}

export function fullName(
  user: { firstname?: string | null; lastname?: string | null; fullname?: string | null } | null | undefined,
): string {
  if (!user) return ''
  if (user.fullname) return user.fullname
  return [user.firstname, user.lastname].filter(Boolean).join(' ')
}

export function formatNumber(value: number | string | null | undefined, digits = 0): string {
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

export function formatCredits(value: number | string | null | undefined): string {
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return n.toFixed(2)
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`
}

export function truncate(text: string, max: number): string {
  if (text.length <= max) return text
  return `${text.slice(0, max - 1)}…`
}

export function stripHtml(html: string | null | undefined): string {
  if (!html) return ''
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent ?? ''
}

export function isGoogleDriveLink(url: string | null | undefined): boolean {
  return !!url && url.toLowerCase().includes('drive.google.com')
}

export function isValidUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

/** Deterministic avatar colour from a name so the same person always gets the same hue. */
export function avatarHue(seed: string | number | null | undefined): number {
  const str = String(seed ?? '')
  let hash = 0
  for (let i = 0; i < str.length; i += 1) hash = (hash * 31 + str.charCodeAt(i)) >>> 0
  return hash % 360
}
